import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, ExternalLink, Star } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import axios from "axios";
import { useTheme } from "@/contexts/ThemeContext";
import { useTranslation } from "@/hooks/useTranslation";
import { formatDateMdY } from "@/utils/dateFormat";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

function fillTemplate(template, vars = {}) {
  if (!template || typeof template !== "string") return template;
  return Object.entries(vars).reduce(
    (str, [key, value]) => str.replaceAll(`{${key}}`, String(value ?? "")),
    template
  );
}

/**
 * Star row for overall rating or a single review (1–5).
 * Supports half-stars for average rating via fill width.
 */
function Stars({ value = 0, size = "md", ariaLabel }) {
  const n = Math.max(0, Math.min(5, Number(value) || 0));
  const sizeCls = size === "sm" ? "h-3.5 w-3.5" : "h-5 w-5";
  return (
    <div className="flex items-center gap-0.5" aria-label={ariaLabel || `${n} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => {
        const fill = Math.max(0, Math.min(1, n - (i - 1)));
        return (
          <span key={i} className={`relative inline-block ${sizeCls}`}>
            <Star className={`${sizeCls} text-gray-400/40`} strokeWidth={1.5} />
            <span
              className="absolute inset-0 overflow-hidden"
              style={{ width: `${fill * 100}%` }}
            >
              <Star
                className={`${sizeCls} text-amber-400 fill-amber-400`}
                strokeWidth={1.5}
              />
            </span>
          </span>
        );
      })}
    </div>
  );
}

function AuthorAvatar({ name, photoUrl, isDark }) {
  const initials = (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt=""
        className="h-10 w-10 rounded-full object-cover shrink-0 border border-white/10"
        loading="lazy"
        referrerPolicy="no-referrer"
      />
    );
  }

  return (
    <div
      className={`h-10 w-10 rounded-full shrink-0 flex items-center justify-center text-xs font-semibold ${
        isDark ? "bg-blue-500/20 text-blue-300" : "bg-red-100 text-red-700"
      }`}
      aria-hidden
    >
      {initials || "?"}
    </div>
  );
}

function ReviewCard({ review, isDark, t, fullWidth = false }) {
  const authorName = review.author_name || "Google user";
  const body = (
    <article
      className={cn(
        "border p-5 flex flex-col gap-3",
        fullWidth ? "w-full h-full" : "w-[min(85vw,22rem)] sm:w-80 shrink-0",
        isDark
          ? "bg-[#0A0A0A] border-white/10"
          : "bg-white border-gray-200 shadow-sm"
      )}
    >
      <div className="flex items-start gap-3">
        <AuthorAvatar
          name={authorName}
          photoUrl={review.author_photo_url}
          isDark={isDark}
        />
        <div className="min-w-0 flex-1">
          {/* Author credit — required Google attribution when showing a review */}
          {review.author_uri ? (
            <a
              href={review.author_uri}
              target="_blank"
              rel="noopener noreferrer"
              className={`font-semibold text-sm truncate block hover:underline ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              {authorName}
            </a>
          ) : (
            <p
              className={`font-semibold text-sm truncate ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              {authorName}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <Stars
              value={review.rating}
              size="sm"
              ariaLabel={fillTemplate(t("googleReviews.starsAria"), {
                n: review.rating ?? 0,
              })}
            />
            {review.relative_time && (
              <span
                className={`text-xs ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                {review.relative_time}
              </span>
            )}
          </div>
        </div>
      </div>
      <p
        className={`text-sm leading-relaxed line-clamp-5 font-manrope ${
          isDark ? "text-gray-300" : "text-gray-700"
        }`}
      >
        {review.text || t("googleReviews.noText")}
      </p>
      {review.google_maps_uri && (
        <a
          href={review.google_maps_uri}
          target="_blank"
          rel="noopener noreferrer"
          className={`mt-auto text-xs font-medium inline-flex items-center gap-1 ${
            isDark ? "text-blue-400 hover:text-blue-300" : "text-red-600 hover:text-red-700"
          }`}
        >
          {t("googleReviews.viewOnGoogle")}
          <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </article>
  );

  return body;
}

/**
 * Phone layout: one review at a time with left/right arrows that slide in place.
 */
function MobileReviewsCarousel({ reviews, isDark, t }) {
  const reduceMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: reviews.length > 1,
    align: "center",
    duration: reduceMotion ? 0 : 22,
  });
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    if (!emblaApi) return undefined;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    onSelect();
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi]);

  const canNav = reviews.length > 1;
  const arrowCls = isDark
    ? "bg-[#0A0A0A] border border-white/15 text-white active:bg-white/10"
    : "bg-white border border-gray-200 text-gray-900 shadow-sm active:bg-gray-100";

  return (
    <div className="md:hidden px-6" data-testid="google-reviews-mobile-carousel">
      <div className="flex items-stretch gap-2">
        {canNav && (
          <button
            type="button"
            aria-label={t("googleReviews.prevReview")}
            onClick={() => emblaApi?.scrollPrev()}
            className={`shrink-0 self-center h-11 w-11 flex items-center justify-center rounded-none touch-manipulation ${arrowCls}`}
            data-testid="google-reviews-prev"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}
        <div className="min-w-0 flex-1 overflow-hidden select-none" ref={emblaRef}>
          <div className="flex items-stretch">
            {reviews.map((review, idx) => (
              <div
                key={`${review.author_name}-${idx}-${review.publish_time || review.relative_time || ""}`}
                className="min-w-0 flex-[0_0_100%]"
                aria-hidden={idx !== selected}
              >
                <ReviewCard review={review} isDark={isDark} t={t} fullWidth />
              </div>
            ))}
          </div>
        </div>
        {canNav && (
          <button
            type="button"
            aria-label={t("googleReviews.nextReview")}
            onClick={() => emblaApi?.scrollNext()}
            className={`shrink-0 self-center h-11 w-11 flex items-center justify-center rounded-none touch-manipulation ${arrowCls}`}
            data-testid="google-reviews-next"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        )}
      </div>
      {canNav && (
        <p
          className={`text-center text-xs mt-3 tabular-nums ${
            isDark ? "text-gray-500" : "text-gray-500"
          }`}
          aria-live="polite"
        >
          {fillTemplate(t("googleReviews.reviewOf"), {
            current: selected + 1,
            total: reviews.length,
          })}
        </p>
      )}
    </div>
  );
}

/**
 * Landing-page Google reviews section.
 * Data comes only from our backend cache (GET /api/reviews) — never Google directly.
 */
export default function GoogleReviews() {
  const { isDark } = useTheme();
  const { t, language } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const lang = language === "es" ? "es" : "en";
        const res = await axios.get(`${API}/reviews`, {
          params: { lang },
          timeout: 15000,
        });
        if (!cancelled) setData(res.data);
      } catch (err) {
        console.error("Failed to load Google reviews:", err);
        if (!cancelled) {
          setError("load_failed");
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [language]);

  const reviews = useMemo(() => {
    const list = Array.isArray(data?.reviews) ? data.reviews : [];
    return list.filter((r) => r && String(r.text || "").trim());
  }, [data]);

  // One sequence of cards (padded if short). Rendered twice in the DOM for a seamless loop.
  const marqueeBase = useMemo(() => {
    if (!reviews.length) return [];
    let base = [...reviews];
    while (base.length > 0 && base.length < 4) {
      base = [...base, ...reviews];
    }
    return base;
  }, [reviews]);

  const mapsUrl =
    data?.google_maps_uri ||
    (data?.place_id
      ? `https://www.google.com/maps/place/?q=place_id:${data.place_id}`
      : "https://www.google.com/maps");

  const hasRating = data?.rating != null;
  const hasContent = hasRating || reviews.length > 0;
  const placeName = data?.place_name || t("googleReviews.defaultPlace");
  const dateLocale = language === "es" ? "es-ES" : "en-US";

  // Empty / not configured: hide section cleanly (no broken block on landing)
  if (!loading && !error && !hasContent) {
    return null;
  }

  return (
    <section
      className={`py-16 md:py-24 overflow-hidden transition-colors duration-300 ${
        isDark ? "bg-[#050505]" : "bg-[#FAFAFA]"
      }`}
      data-testid="google-reviews-section"
      aria-label={t("googleReviews.sectionAria")}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6"
        >
          <div>
            <span
              className={`text-sm uppercase tracking-widest font-medium mb-3 block ${
                isDark ? "text-blue-500" : "text-red-600"
              }`}
            >
              {t("googleReviews.eyebrow")}
            </span>
            <h2
              className={`font-unbounded text-3xl md:text-4xl font-bold ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              {t("googleReviews.title")}
            </h2>
            <p className={`mt-3 max-w-lg ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              {fillTemplate(t("googleReviews.description"), { place: placeName })}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            {loading ? (
              <div
                className={`h-14 w-48 animate-pulse ${
                  isDark ? "bg-[#1A1A1A]" : "bg-gray-200"
                }`}
              />
            ) : hasRating ? (
              <div
                className={`flex items-center gap-4 px-5 py-3 border ${
                  isDark
                    ? "bg-[#0A0A0A] border-white/10"
                    : "bg-white border-gray-200 shadow-sm"
                }`}
              >
                <div>
                  <div className="flex items-baseline gap-2">
                    <span
                      className={`font-unbounded text-3xl font-bold ${
                        isDark ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {Number(data.rating).toFixed(1)}
                    </span>
                    <Stars
                      value={data.rating}
                      size="md"
                      ariaLabel={fillTemplate(t("googleReviews.starsAria"), {
                        n: Number(data.rating).toFixed(1),
                      })}
                    />
                  </div>
                  {data.user_rating_count != null && (
                    <p
                      className={`text-xs mt-1 ${
                        isDark ? "text-gray-500" : "text-gray-500"
                      }`}
                    >
                      {fillTemplate(t("googleReviews.basedOn"), {
                        count: Number(data.user_rating_count).toLocaleString(dateLocale),
                      })}
                    </p>
                  )}
                </div>
              </div>
            ) : null}

            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold uppercase tracking-wider transition-colors ${
                isDark
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : "bg-red-600 hover:bg-red-700 text-white"
              }`}
              data-testid="see-all-google-reviews-btn"
            >
              {t("googleReviews.seeAll")}
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </motion.div>
      </div>

      {loading && (
        <>
          <div className="md:hidden px-6">
            <div
              className={`h-48 border animate-pulse ${
                isDark
                  ? "bg-[#0A0A0A] border-white/10"
                  : "bg-white border-gray-200"
              }`}
            />
          </div>
          <div className="hidden md:flex gap-4 px-6 md:px-12 overflow-hidden">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={`w-80 shrink-0 h-48 border animate-pulse ${
                  isDark
                    ? "bg-[#0A0A0A] border-white/10"
                    : "bg-white border-gray-200"
                }`}
              />
            ))}
          </div>
        </>
      )}

      {!loading && error && (
        <p
          className={`text-center text-sm px-6 ${
            isDark ? "text-gray-500" : "text-gray-500"
          }`}
        >
          {t("googleReviews.loadError")}
        </p>
      )}

      {!loading && !error && reviews.length > 0 && (
        <>
          <MobileReviewsCarousel
            key={language}
            reviews={reviews}
            isDark={isDark}
            t={t}
          />

          <div className="relative hidden md:block">
            {/* Edge fade for polish */}
            <div
              className={`pointer-events-none absolute inset-y-0 left-0 w-12 md:w-24 z-10 ${
                isDark
                  ? "bg-gradient-to-r from-[#050505] to-transparent"
                  : "bg-gradient-to-r from-[#FAFAFA] to-transparent"
              }`}
            />
            <div
              className={`pointer-events-none absolute inset-y-0 right-0 w-12 md:w-24 z-10 ${
                isDark
                  ? "bg-gradient-to-l from-[#050505] to-transparent"
                  : "bg-gradient-to-l from-[#FAFAFA] to-transparent"
              }`}
            />

            <div className="google-reviews-marquee group">
              <div
                className="google-reviews-marquee-track"
                style={{ animationDuration: `${Math.max(45, marqueeBase.length * 7)}s` }}
              >
                {/* Two identical sets → CSS moves by -50% of track width for a seamless loop */}
                {[0, 1].map((setIndex) => (
                  <div
                    key={`set-${setIndex}`}
                    className="google-reviews-marquee-set"
                    aria-hidden={setIndex === 1 ? true : undefined}
                  >
                    {marqueeBase.map((review, idx) => (
                      <ReviewCard
                        key={`${setIndex}-${review.author_name}-${idx}-${review.publish_time || review.relative_time || ""}`}
                        review={review}
                        isDark={isDark}
                        t={t}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Required Google attribution */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p
          className={`text-xs flex items-center gap-2 ${
            isDark ? "text-gray-500" : "text-gray-500"
          }`}
        >
          <span
            className={`inline-flex items-center justify-center h-5 w-5 rounded-sm text-[10px] font-bold ${
              isDark ? "bg-white text-gray-900" : "bg-white border border-gray-200 text-gray-800"
            }`}
            aria-hidden
          >
            G
          </span>
          {t("googleReviews.attribution")}
        </p>
        {data?.last_updated && (
          <p className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}>
            {fillTemplate(t("googleReviews.updated"), {
              date: formatDateMdY(data.last_updated),
            })}
          </p>
        )}
      </div>
    </section>
  );
}
